import useSocketClient from '@/hooks/use-socket-client';
import { SocketClient } from '@/services/socket-client';
import { SocketStatusEnum } from '@/types';
import { useParams } from 'next/navigation';
import React, { createContext, useContext } from 'react';

interface ISocketClientProviderContext {
  setLatestPublishedContentHash: (value: string) => void;
  connectSocket: (_ddocId: string) => Promise<void>;
  storeUpdatesOnSocket: (update: string) => Promise<void>;
  socketClient: SocketClient | null;
  deleteUpdatesBeforeLatestIpfsUpload: (
    fileId: number,
    ddocId: string
  ) => Promise<void>;
  selectUpdatesForDeletion: () => void;
  isSyncingUpdateToSocket: () => boolean;
  resetSocketStates: () => void;
  contentFromSocket: string[];
  isSocketConnected: () => boolean;
  setDocOnchainId: (id: number | null) => void;
  removeAppliedUpdates: (_offset: number) => void;
  getStateLatestIpfsHash: () => string | null;
  setDocRtcKey: (
    docKey: string,
    ddocId: string,
    shouldDecrypt?: boolean
  ) => Promise<void>;
  getDocRtcKey: () => null | Uint8Array;
  isRtcKeyOnWs: () => boolean;
  setIsKeyOnWs: (_val: boolean) => void;
}

const SocketClientProviderContext = createContext<ISocketClientProviderContext>(
  {
    setLatestPublishedContentHash: () => null,
    connectSocket: async () => {},
    storeUpdatesOnSocket: async () => {},
    socketClient: null,
    selectUpdatesForDeletion: () => null,
    deleteUpdatesBeforeLatestIpfsUpload: async () => {},
    isSyncingUpdateToSocket: () => false,
    resetSocketStates: () => {},
    contentFromSocket: [],
    isSocketConnected: () => false,
    removeAppliedUpdates: () => null,
    setDocOnchainId: () => null,
    getStateLatestIpfsHash: () => '',
    setDocRtcKey: async () => {},
    getDocRtcKey: () => null,
    isRtcKeyOnWs: () => false,
    setIsKeyOnWs: () => null,
  }
);

export const useSocketClientContext = () => {
  return useContext(SocketClientProviderContext);
};

const SocketClientProvider = ({ children }: { children: React.ReactNode }) => {
  const { ddocId: activeDocId } = useParams() as { ddocId: string };

  const {
    connectSocket,
    setLatestPublishedContentHash,
    socketClient,
    deleteUpdatesBeforeLatestIpfsUpload,
    storeUpdatesOnSocket,
    selectUpdatesForDeletion,
    isSyncingUpdateRef,
    contentFromSocket,
    setContentFromSocket,
    setDocOnchainId,
    removeAppliedUpdates,
    getStateLatestIpfsHash,
    resetUpdateIdsTobeDeleted,
    resetSyncedUpdateIdTrackerRef,
    resetDocOnchainId,
    setDocRtcKey,
    resetDocRtcKey,
    getDocRtcKey,
    resetUpdatesFromHistory,
    isRtcKeyOnWs,
    setIsKeyOnWs,
    resetUnsyncedUpdateQueue,
    resetUpdateFromPeerYetTobeAppliedOnTheEditor,
  } = useSocketClient(activeDocId);

  const isSocketConnected = () => {
    return socketClient?._webSocketStatus === SocketStatusEnum.CONNECTED;
  };

  const resetSocketStates = () => {
    socketClient?.disconnect('SWITCH ROUTE');
    isSyncingUpdateRef.current = null;
    setLatestPublishedContentHash('');
    setContentFromSocket([]);
    resetUpdateIdsTobeDeleted();
    resetSyncedUpdateIdTrackerRef();
    resetDocOnchainId();
    resetDocRtcKey();
    resetUpdatesFromHistory();
    setIsKeyOnWs(false);
    resetUnsyncedUpdateQueue();
    resetUpdateFromPeerYetTobeAppliedOnTheEditor();
  };

  const isSyncingUpdateToSocket = () => {
    return !!isSyncingUpdateRef.current;
  };
  return (
    <SocketClientProviderContext.Provider
      value={{
        isRtcKeyOnWs,
        setIsKeyOnWs,
        connectSocket,
        setLatestPublishedContentHash,
        socketClient,
        deleteUpdatesBeforeLatestIpfsUpload,
        storeUpdatesOnSocket,
        selectUpdatesForDeletion,
        isSyncingUpdateToSocket,
        resetSocketStates,
        contentFromSocket,
        isSocketConnected,
        setDocOnchainId,
        removeAppliedUpdates,
        getStateLatestIpfsHash,
        getDocRtcKey,
        setDocRtcKey,
      }}
    >
      {children}
    </SocketClientProviderContext.Provider>
  );
};

export default SocketClientProvider;
